import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ImageStoreService } from '@imagise/generated-imagise-cloud-api-client';
import { Observable, map, of, throwError, catchError, switchMap } from 'rxjs';
import { FileResult } from '@imagise/entities';
import { loadImage } from '@imagise/shared';
import { checkOperationResponse } from '../error-reporting/operation-request';

/** Viewer service */
@Injectable({
  providedIn: 'root',
})
export class ViewerService {
  #imageStoreService = inject(ImageStoreService);

  private readonly isBrowser: boolean;

  private currentPreviewBlobUrl: string | null = null;

  constructor() {
    const platformId = inject(PLATFORM_ID);

    this.isBrowser = isPlatformBrowser(platformId);
  }

  /**
   * Gets image metadata for the current preview source.
   * The current editor flow operates with a single preview page.
   */
  getImageInfo(fileInfo: FileResult): Observable<ImageInfo> {
    this.assertFileIsValid(fileInfo);

    return this.viewImage(fileInfo, 0).pipe(
      map((previewInfo) => {
        const format = this.extractFileExtension(fileInfo.FileName) || 'PNG';
        return {
          FileName: fileInfo.FileName,
          Format: format,
          Width: previewInfo.width,
          Height: previewInfo.height,
          HasAlpha: true,
          ImageType: 'Bitmap' as const,
          PageType: 'SinglePage' as const,
          NumPages: 1,
          DefaultDestinationFormat: format,
        };
      })
    );
  }

  /**
   * Requests the image preview.
   * The current editor flow supports only the first page preview.
   */
  viewImage(
    fileInfo: FileResult,
    pageIndex: number,
    _flags?: string[]
  ): Observable<ViewerImageInfo> {
    this.assertFileIsValid(fileInfo);

    if (pageIndex !== 0) {
      return throwError(
        () =>
          new Error(
            'Preview supports a single page with the current API integration.'
          )
      );
    }

    return this.resolvePreviewUrl(fileInfo).pipe(
      switchMap((previewUrl) =>
        this.resolveImageSize(previewUrl).pipe(
          map(({ width, height }) => ({
            ImagePath: fileInfo.FileName,
            ImageSize: `${width}x${height}`,
            SharedURL: '',
            url: previewUrl,
            fileName: fileInfo.FileName,
            width,
            height,
            pageIndex,
            numPages: 1,
          }))
        )
      )
    );
  }

  private resolvePreviewUrl(fileInfo: FileResult): Observable<string> {
    if (fileInfo.DownloadUrl) {
      return of(fileInfo.DownloadUrl);
    }

    if (fileInfo.UploadFileName) {
      return of(fileInfo.UploadFileName);
    }

    if (fileInfo.LocalObjectUrl) {
      return of(fileInfo.LocalObjectUrl);
    }

    if (fileInfo.SourceBase64) {
      const mimeType = fileInfo.SourceMimeType || 'image/png';
      return of(`data:${mimeType};base64,${fileInfo.SourceBase64}`);
    }

    if (fileInfo.id) {
      return this.#imageStoreService
        .download(encodeURIComponent(fileInfo.id))
        .pipe(checkOperationResponse(), map((blob) => this.createPreviewBlobUrl(blob)));
    }

    return throwError(() => new Error('Cannot resolve preview URL for file.'));
  }

  private createPreviewBlobUrl(blob: Blob): string {
    if (this.currentPreviewBlobUrl) {
      URL.revokeObjectURL(this.currentPreviewBlobUrl);
      this.currentPreviewBlobUrl = null;
    }

    const objectUrl = URL.createObjectURL(blob);
    this.currentPreviewBlobUrl = objectUrl;
    return objectUrl;
  }

  private resolveImageSize(
    imageUrl: string
  ): Observable<{ width: number; height: number }> {
    if (!this.isBrowser) {
      return of({ width: 0, height: 0 });
    }

    return loadImage(imageUrl).pipe(
      map((image) => ({
        width: image.naturalWidth || image.width || 0,
        height: image.naturalHeight || image.height || 0,
      })),
      catchError(() => of({ width: 0, height: 0 }))
    );
  }

  private assertFileIsValid(fileInfo: FileResult): void {
    if (!fileInfo) {
      throw new Error('fileInfo is null');
    }

    if (!fileInfo.IsSuccess) {
      throw new Error(
        `specified fileInfo has the following error: "${fileInfo.idError}"`
      );
    }
  }

  private extractFileExtension(fileName: string): string {
    const parts = fileName.split('.');
    const extension = parts.length > 1 ? parts[parts.length - 1] : '';
    return extension.toUpperCase();
  }
}

/** Viewer image info */
export interface ViewerImageInfo {
  /** Path of the image file */
  ImagePath: string;

  /** Image size information */
  ImageSize: string;

  /** Shared Image URL */
  SharedURL: string;

  /** Image URL */
  url: string;

  /** File name */
  fileName: string;

  /** Image width in pixels */
  width: number;

  /** Image height in pixels */
  height: number;

  /** Zero-based page index */
  pageIndex: number;

  /** Page count */
  numPages?: number;
}

export interface ImageInfo {
  /** File name */
  FileName: string;

  /** Image format */
  Format: string;

  /** Image width */
  Width: number;

  /** Image height */
  Height: number;

  /** Has alpha sign */
  HasAlpha: boolean;

  /** Image type */
  ImageType: 'Undefined' | 'Bitmap' | 'Vector';

  /** Page type */
  PageType: 'SinglePage' | 'MultiPage' | 'Animation';

  /** Number of pages */
  NumPages: number;

  /** Default export format */
  DefaultDestinationFormat: string;
}
